import { Button, FocusTrap, HStack, Text, VStack } from "@chakra-ui/react"
import { ReactNode, useId, useRef } from "react"

export type ConfirmOverlayProps = {
  /** The question, e.g. "Discard your changes?" */
  message: ReactNode
  /** The action being confirmed, e.g. "Yes, discard". Takes focus. */
  confirmLabel: ReactNode
  /** The way back, e.g. "No, go back". */
  cancelLabel: ReactNode
  onConfirm: () => void
  onCancel: () => void
}

/**
 * A confirmation laid over the thing it is about, instead of a dialog. Render
 * it inside a `position: relative` container; it covers that container and
 * inherits its corner radius.
 *
 * Use it when leaving an in-place editor or flow would lose work, typically
 * from a click outside it. The Home "Add Path" flow uses the same pattern.
 */
export default function ConfirmOverlay({
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel
}: ConfirmOverlayProps) {
  const messageId = useId()
  const confirmRef = useRef<HTMLButtonElement>(null)
  return (
    <FocusTrap
      initialFocus={() => confirmRef.current}
      returnFocusOnDeactivate={false}
    >
      <VStack
        role="alertdialog"
        aria-labelledby={messageId}
        aria-modal="true"
        justify="center"
        pos="absolute"
        // Reaches over the container's border so no edge of it shows through.
        inset="-1px"
        zIndex={100}
        gap={2}
        p={3}
        borderRadius="inherit"
        bg="blackAlpha.700"
        backdropFilter="blur(20px)"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation()
            onCancel()
          }
        }}
      >
        <Text
          id={messageId}
          color="white"
          fontSize="sm"
          lineHeight="short"
          textAlign="center"
          m={0}
        >
          {message}
        </Text>
        <HStack gap={1}>
          <Button
            ref={confirmRef}
            size="sm"
            variant="subtle"
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            colorPalette="whiteAlpha"
            color="whiteAlpha.900"
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
        </HStack>
      </VStack>
    </FocusTrap>
  )
}
